import { lazy, Suspense, useEffect, useMemo } from "react";
import { Outlet, useLocation } from "react-router-dom";
import { PageHead } from "@/components/PageHead";
import SiteBackground from "@/components/SiteBackground";
import SiteHeader from "@/components/SiteHeader";
import { PageTransitionProvider } from "@/components/PageTransition";
import { getPageMetadata } from "@/lib/pageMetadataRegistry";
import {
  PageMetadataProvider,
  usePageMetadataOverride,
} from "@/lib/pageMetadataContext";
import {
  initExecuraSiteAnalytics,
  trackExecuraPageView,
} from "@/lib/execuraSiteAnalytics";

const ChatWidget = lazy(() =>
  import("@/components/ChatWidget").then((m) => ({ default: m.ChatWidget })),
);

const Analytics = lazy(() =>
  import("@vercel/analytics/react").then((m) => ({ default: m.Analytics })),
);

function shouldLoadAnalytics() {
  if (typeof window === "undefined") return false;
  const host = window.location.hostname;
  return host !== "localhost" && host !== "127.0.0.1";
}

const GTM_ID = process.env.NEXT_PUBLIC_GTM_ID;

function trackPageView(pathname: string) {
  if (typeof window === "undefined") return;
  const w = window as Window & { dataLayer?: Record<string, unknown>[] };
  if (w.dataLayer) {
    w.dataLayer.push({ event: "page_view", page_path: pathname });
  }
}

function SiteShellInner({ hideChrome = false }: { hideChrome?: boolean }) {
  const location = useLocation();
  const isAdmin = location.pathname.startsWith("/admin");
  const isDeck = location.pathname === "/deck" || location.pathname.startsWith("/deck/");
  const { override, setOverride } = usePageMetadataOverride();
  const registryMetadata = useMemo(
    () => getPageMetadata(location.pathname),
    [location.pathname],
  );
  const pageMetadata = override ?? registryMetadata;

  useEffect(() => {
    initExecuraSiteAnalytics();
  }, []);

  useEffect(() => {
    setOverride(null);
    trackPageView(location.pathname);
    trackExecuraPageView(location.pathname + location.search);
  }, [location.pathname, location.search, setOverride]);

  return (
    <>
      <PageHead metadata={pageMetadata} />
      {GTM_ID ? (
        <>
          <script
            dangerouslySetInnerHTML={{
              __html: `(function(w,d,s,l,i){w[l]=w[l]||[];w[l].push({'gtm.start':
new Date().getTime(),event:'gtm.js'});var f=d.getElementsByTagName(s)[0],
j=d.createElement(s),dl=l!='dataLayer'?'&l='+l:'';j.async=true;j.src=
'https://www.googletagmanager.com/gtm.js?id='+i+dl;f.parentNode.insertBefore(j,f);
})(window,document,'script','dataLayer','${GTM_ID}');`,
            }}
          />
          <noscript>
            <iframe
              src={`https://www.googletagmanager.com/ns.html?id=${GTM_ID}`}
              height="0"
              width="0"
              style={{ display: "none", visibility: "hidden" }}
              title="Google Tag Manager"
            />
          </noscript>
        </>
      ) : null}
      {isAdmin || isDeck || hideChrome ? (
        <Outlet />
      ) : (
        <PageTransitionProvider>
          <SiteBackground />
          <SiteHeader />
          <div className="relative z-[1] pt-[var(--site-header-height)]">
            <a href="#main-content" className="skip-link">
              Skip to main content
            </a>
            <Outlet />
            <Suspense fallback={null}>
              <ChatWidget />
            </Suspense>
          </div>
        </PageTransitionProvider>
      )}
      {shouldLoadAnalytics() ? (
        <Suspense fallback={null}>
          <Analytics />
        </Suspense>
      ) : null}
    </>
  );
}

export function SiteShell({ hideChrome = false }: { hideChrome?: boolean }) {
  return (
    <PageMetadataProvider>
      <SiteShellInner hideChrome={hideChrome} />
    </PageMetadataProvider>
  );
}
